export function createAsyncActionFlow({
    defaultTriggerLabel,
    feedbackController,
    root,
    stateConfig,
    stateDatasetKey = 'asyncState',
    timerApi = globalThis,
    trigger,
}) {
    const states = normalizeStateConfig(stateConfig, defaultTriggerLabel);
    let currentState = readInitialState(root, stateDatasetKey, states);
    let cooldownTimer = null;

    const applyState = (state) => {
        const config = states[state];

        root.dataset[stateDatasetKey] = state;
        trigger.disabled = config.disabled;
        trigger.textContent = config.label;
        currentState = state;
    };

    const clearCooldownTimer = () => {
        if (cooldownTimer === null) {
            return;
        }

        timerApi.clearTimeout(cooldownTimer);
        cooldownTimer = null;
    };

    const scheduleCooldownReset = (cooldownSeconds) => {
        if (!Number.isFinite(cooldownSeconds) || cooldownSeconds <= 0) {
            return;
        }

        clearCooldownTimer();
        cooldownTimer = timerApi.setTimeout(() => {
            cooldownTimer = null;
            transition('idle', { clearFeedback: true });
        }, Math.ceil(cooldownSeconds * 1000));
    };

    const transition = (state, options = {}) => {
        if (!(state in states)) {
            return;
        }

        if (state !== 'rate-limited') {
            clearCooldownTimer();
        }

        applyState(state);

        if (typeof options.renderContent === 'function') {
            feedbackController.showContent(options.tone ?? 'info', options.renderContent);
        } else if (typeof options.message === 'string' && options.message.trim() !== '') {
            feedbackController.showMessage(options.message, options.tone ?? 'info');
        } else if (options.clearFeedback === true) {
            feedbackController.clear();
        }

        if (state === 'rate-limited') {
            scheduleCooldownReset(options.cooldownSeconds ?? null);
        }
    };

    applyState(currentState);

    return {
        destroy() {
            clearCooldownTimer();
        },
        getState() {
            return currentState;
        },
        isLocked() {
            return currentState === 'generating' || currentState === 'rate-limited';
        },
        transition,
    };
}

function normalizeStateConfig(stateConfig, defaultTriggerLabel) {
    const fallbackLabel = String(defaultTriggerLabel ?? '').trim() || 'Submit';
    const config = typeof stateConfig === 'object' && stateConfig !== null ? stateConfig : {};

    return {
        idle: normalizeStateDefinition(config.idle, {
            disabled: false,
            label: fallbackLabel,
        }),
        generating: normalizeStateDefinition(config.generating, {
            disabled: true,
            label: 'Working...',
        }),
        ready: normalizeStateDefinition(config.ready, {
            disabled: false,
            label: fallbackLabel,
        }),
        failed: normalizeStateDefinition(config.failed, {
            disabled: false,
            label: fallbackLabel,
        }),
        'rate-limited': normalizeStateDefinition(config['rate-limited'], {
            disabled: true,
            label: fallbackLabel,
        }),
    };
}

function normalizeStateDefinition(candidate, fallback) {
    if (typeof candidate !== 'object' || candidate === null) {
        return fallback;
    }

    return {
        disabled: candidate.disabled === true,
        label: typeof candidate.label === 'string' && candidate.label.trim() !== ''
            ? candidate.label
            : fallback.label,
    };
}

function readInitialState(root, stateDatasetKey, states) {
    const initialState = String(root.dataset[stateDatasetKey] ?? '').trim();

    if (initialState in states) {
        return initialState;
    }

    return 'idle';
}
